import { Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import Button from "../../Button";
import Tooltip from "../../Tooltip";
import "./transferList.scss";
import TransferListAside from "./TransferListAside";

const CustomTransferList = ({
  label,
  label1,
  label2,
  items,
  defaultSelectedValue,
  maxItems,
  maxValue,
  field,
  onChange,
  readyOnly,
  single = false,
}) => {
  const [leftOptions, setLeftOptions] = useState([]);
  const [rightOptions, setRightOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [searchLeft, setSearchLeft] = useState("");
  const [searchRight, setSearchRight] = useState("");

  useEffect(() => {
    if (defaultSelectedValue?.length && items.length) {
      const leftItems = items.filter((item) => {
        const fieldMatched = defaultSelectedValue.filter(
          (sel) => sel[field] === item[field]
        );
        return !fieldMatched?.length;
      });
      setLeftOptions(leftItems);
      leftItems.length + defaultSelectedValue.length === items.length
        ? setRightOptions(defaultSelectedValue)
        : setRightOptions([]);
      onChange(defaultSelectedValue);
    } else {
      setLeftOptions(items);
      setRightOptions([]);
    }
  }, [items]);

  useEffect(() => {
    const leftSelected = leftOptions.filter((left) =>
      selected.includes(left[field])
    );
    if (!leftSelected?.length) return;
    const leftWeight = leftSelected.reduce(
      (a, b) => a + parseFloat(b.weight),
      0
    );
    const rightWeight = rightOptions.reduce(
      (a, b) => a + parseFloat(b.weight),
      0
    );
    leftWeight + rightWeight > maxValue ||
    (maxItems && leftSelected.length + rightOptions.length > maxItems)
      ? setDisabled(true)
      : setDisabled(false);
  }, [selected, leftOptions, rightOptions, maxItems, maxValue, field]);

  const handleToggle = (item) => {
    const currentIndex = selected.indexOf(item);
    const newSelected = [...selected];

    if (currentIndex === -1) {
      newSelected.push(item);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelected(newSelected);
  };

  const addSelectedItems = () => {
    const leftSelected = leftOptions.filter((left) =>
      selected.includes(left[field])
    );
    if(single){ 
      if(rightOptions.length < 1 && leftSelected.length < 2) {
        
        const leftRemain = leftOptions.filter(
          (left) => !selected.includes(left[field])
        );
        const selectedRemain = selected.filter((item) =>
          leftOptions.every((left) => left[field] !== item)
        );
        setRightOptions([...rightOptions, ...leftSelected]);
        setLeftOptions([...leftRemain]);
        setSelected([...selectedRemain]);
        if (onChange) {
          onChange([...rightOptions, ...leftSelected]);
        }
      }
    } else {
      const leftRemain = leftOptions.filter(
        (left) => !selected.includes(left[field])
      );
      const selectedRemain = selected.filter((item) =>
        leftOptions.every((left) => left[field] !== item)
      );
      setRightOptions([...rightOptions, ...leftSelected]);
      setLeftOptions([...leftRemain]);
      setSelected([...selectedRemain]);
      if (onChange) {
        onChange([...rightOptions, ...leftSelected]);
      }
    }
  };

  const removeSelectedItems = () => {
    const rightSelected = rightOptions.filter((right) =>
      selected.includes(right[field])
    );
    const rightRemain = rightOptions.filter(
      (right) => !selected.includes(right[field])
    );
    const selectedRemain = selected.filter((item) =>
      rightOptions.every((right) => right[field] !== item)
    );
    setLeftOptions([...leftOptions, ...rightSelected]);
    setRightOptions([...rightRemain]);
    setSelected([...selectedRemain]);
    if (onChange) {
      onChange([...rightRemain]);
    }
  };

  const clearSelections = () => {
    setSearchLeft("");
    setSearchRight("");
    setLeftOptions([...leftOptions, ...rightOptions]);
    setRightOptions([]);
    onChange([]);
  };

  const getCurrentValues = useMemo(() => {
    const rightWeight = rightOptions?.reduce(
      (a, b) => a + parseFloat(b.weight),
      0
    );
    const leftSelected = leftOptions.filter((left) =>
      selected.includes(left[field])
    );
    const selectedWeight = leftSelected?.reduce(
      (a, b) => a + parseFloat(b.weight),
      0
    );
    return maxValue - (rightWeight + selectedWeight);
  }, [selected, rightOptions, leftOptions, field, maxValue]);

  const tooltipConfig = {
    content: "Clear selections",
    direction: "top",
  };

  return (
    <div className="transfer-list">
      <p className="transfer-list-label">{label}</p>
      <Grid container spacing={1} justifyContent="center" alignItems="center">
        <Grid item xs={5}>
          <TransferListAside
            title={label1}
            field={field}
            items={leftOptions}
            selected={selected}
            filterText={searchLeft}
            currentLimit={getCurrentValues}
            onFilterChange={(text) => setSearchLeft(text)}
            onSelect={handleToggle}
          />
        </Grid>
        <Grid item xs={2}>
          <Grid container direction="column" alignItems="center">
            <div className="transfer-list-control">
              <Tooltip {...tooltipConfig}>
                <Button
                  size="mini"
                  type="button"
                  onClick={clearSelections}
                  disabled={readyOnly}
                >
                  {"×"}
                </Button>
              </Tooltip>
              <Button
                size="mini"
                type="button"
                onClick={addSelectedItems}
                disabled={disabled || readyOnly}
              >
                {">>"}
              </Button>
              <Button
                size="mini"
                type="button"
                onClick={removeSelectedItems}
                disabled={readyOnly}
              >
                {"<<"}
              </Button>
            </div>
          </Grid>
        </Grid>
        <Grid item xs={5}>
          <TransferListAside
            title={label2}
            field={field}
            items={rightOptions}
            selected={selected}
            filterText={searchRight}
            onFilterChange={(text) => setSearchRight(text)}
            onSelect={handleToggle}
          />
        </Grid>
      </Grid>
    </div>
  );
};

CustomTransferList.propTypes = {
  label: PropTypes.string,
  label1: PropTypes.string,
  label2: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
  maxItems: PropTypes.number,
  maxValue: PropTypes.number,
  field: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  selectedValue: PropTypes.array,
  readyOnly: PropTypes.bool,
};

export default CustomTransferList;
